<template>
  <Dialog v-model="dialogVisible" title="指定区域代理" width="560px">
    <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="formRules" label-width="100px">
      <el-form-item label="会员" prop="userId">
        <el-select
          v-model="formData.userId"
          class="w-1/1"
          clearable
          filterable
          remote
          reserve-keyword
          placeholder="输入编号或昵称搜索会员"
          :remote-method="debouncedSearch"
          :loading="searchLoading"
        >
          <el-option
            v-for="item in options"
            :key="item.id"
            :label="`${item.id} - ${item.nickname || '未命名'}`"
            :value="item.id"
          />
        </el-select>
        <div class="el-form-item__tip">须已是分销商；须选类型与辖区</div>
      </el-form-item>
      <el-form-item label="代理类型" prop="agentType">
        <el-select v-model="formData.agentType" class="w-1/1" @change="onAgentTypeChange">
          <el-option
            v-for="o in AREA_AGENT_TYPE"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="辖区" prop="areaIds">
        <el-cascader
          v-model="areaPathList"
          class="w-1/1"
          :options="cascaderOptions"
          :props="cascaderProps"
          clearable
          filterable
          :disabled="!formData.agentType"
          placeholder="可多选同级行政区"
          @change="onAreaChange"
        />
      </el-form-item>
      <el-form-item label="等级" prop="levelId">
        <el-select v-model="formData.levelId" class="w-1/1" clearable placeholder="默认等级可不选">
          <el-option v-for="l in levels" :key="l.id" :label="l.name" :value="l.id" />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submit">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { getAreaTree } from '@/api/system/area'
import * as UserApi from '@/api/mall/trade/areaAgent/user'
import * as LevelApi from '@/api/mall/trade/areaAgent/level'
import * as MemberUserApi from '@/api/member/user'
import { AREA_AGENT_TYPE } from '../constants'

defineOptions({ name: 'AreaAgentUserCreateForm' })

const emit = defineEmits(['success'])
const message = useMessage()
const dialogVisible = ref(false)
const formLoading = ref(false)
const searchLoading = ref(false)
const options = ref<MemberUserApi.UserVO[]>([])
const levels = ref<LevelApi.AreaAgentLevelSimpleVO[]>([])
const areaTree = ref<any[]>([])
const areaPathList = ref<number[][]>([])
const formRef = ref()
const formData = ref<{
  userId?: number
  agentType?: number
  areaIds: number[]
  levelId?: number
}>({ areaIds: [] })

const formRules = reactive({
  userId: [{ required: true, message: '请选择会员', trigger: 'change' }],
  agentType: [{ required: true, message: '请选择代理类型', trigger: 'change' }],
  areaIds: [{ required: true, type: 'array', min: 1, message: '请选择辖区', trigger: 'change' }]
})

const cascaderProps = {
  value: 'id',
  label: 'name',
  children: 'children',
  multiple: true,
  checkStrictly: true,
  emitPath: true
}

const cascaderOptions = computed(() => {
  const depth = formData.value.agentType === 1 ? 1 : formData.value.agentType === 2 ? 2 : 3
  return clipTree(areaTree.value, depth)
})

const clipTree = (nodes: any[], depth: number, level = 1): any[] => {
  if (!nodes?.length || depth < 1) return []
  return nodes.map((n) => {
    const item: any = { id: n.id, name: n.name }
    if (level < depth && n.children?.length) {
      item.children = clipTree(n.children, depth, level + 1)
    }
    return item
  })
}

let timer: ReturnType<typeof setTimeout> | null = null
const debouncedSearch = (query: string) => {
  if (timer) clearTimeout(timer)
  timer = setTimeout(async () => {
    if (!query) {
      options.value = []
      return
    }
    searchLoading.value = true
    try {
      const data = await MemberUserApi.getUserPage({
        pageNo: 1,
        pageSize: 20,
        nickname: /^\d+$/.test(query) ? undefined : query,
        id: /^\d+$/.test(query) ? Number(query) : undefined
      } as any)
      options.value = data.list || []
    } finally {
      searchLoading.value = false
    }
  }, 300)
}

const onAgentTypeChange = () => {
  areaPathList.value = []
  formData.value.areaIds = []
}

const onAreaChange = (val: number[][] | null) => {
  if (!val?.length) {
    formData.value.areaIds = []
    return
  }
  formData.value.areaIds = [...new Set(val.map((p) => p[p.length - 1]).filter(Boolean))]
}

const open = async () => {
  dialogVisible.value = true
  formData.value = { areaIds: [], agentType: 1 }
  areaPathList.value = []
  if (!areaTree.value.length) {
    areaTree.value = (await getAreaTree()) || []
  }
  levels.value = (await LevelApi.getAreaAgentLevelSimpleList()) || []
  await nextTick()
  formRef.value?.resetFields()
}

const submit = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    await UserApi.checkAreaAgentAreaConflict({
      agentType: formData.value.agentType!,
      areaIds: formData.value.areaIds
    })
    await UserApi.createAreaAgentUser({
      userId: formData.value.userId!,
      agentType: formData.value.agentType!,
      areaIds: formData.value.areaIds,
      levelId: formData.value.levelId
    })
    message.success('指定成功')
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

defineExpose({ open })
</script>
